import { GlobalOutlined, PhoneOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Checkbox,
  Col,
  ConfigProvider,
  Flex,
  Form,
  Grid,
  Image,
  Input,
  Layout,
  Row,
  Select,
  Typography,
  theme,
} from 'antd';
import type { InputRef } from 'antd';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../config/firebase';
import { mouDarkLogo } from '../../images/AppImages';
import { getFirebaseErrorMessage } from '../../utils/firebaseErrorMessages';

const { Title } = Typography;

function normalizePhone(raw: string): string {
  return String(raw ?? '').replace(/\D+/g, '');
}
function isValidPhoneDigits(p: string): boolean {
  return /^\d{9,15}$/.test(p);
}

const LoginPage = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);
  const screens = Grid.useBreakpoint();
  const isMobile = !!screens.xs && !screens.sm;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const rememberPhone = (values: any) => {
    if (values.remember) localStorage.setItem('rememberedPhone', values.phone);
    else localStorage.removeItem('rememberedPhone');
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const phone = normalizePhone(values.phone);
      if (!isValidPhoneDigits(phone)) {
        message.error(t('login.validPhoneError'));
        return;
      }
      const email = `${phone}@mou.com`;
      const password = phone;
      await signInWithEmailAndPassword(auth, email, password);
      const qUsers = query(collectionGroup(db, 'Users'), where('phone', '==', phone));
      const snap = await getDocs(qUsers);
      if (snap.empty) {
        await signOut(auth);
        message.error(t('login.phoneNotRegistered'));
        return;
      }
      const firstActiveDoc = snap.docs.find((d) => (d.data() as any)?.active === true);
      if (!firstActiveDoc) {
        await signOut(auth);
        message.error(t('login.accountInactive'));
        return;
      }
      rememberPhone(values);
      navigate('/chat');
    } catch (error: any) {
      const friendly = getFirebaseErrorMessage?.(error?.code) || t('login.loginError');
      message.error(friendly);
      try { await signOut(auth); } catch { }
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const rememberedPhone = localStorage.getItem('rememberedPhone');
    if (rememberedPhone) form.setFieldsValue({ phone: rememberedPhone, remember: true });

    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) i18n.changeLanguage(savedLanguage);
  }, [form, i18n]);

  useEffect(() => {
    if (isMobile) inputRef.current?.focus({ preventScroll: true });
  }, [isMobile]);

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#0b82d5',
          colorTextPlaceholder: 'rgba(255,255,255,0.65)',
        },
        components: {
          Input: {
            activeBg: 'transparent',
            hoverBg: 'transparent',
          },
        },
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        <Layout.Content>
          <Flex vertical align="center" justify="center" style={{ minHeight: '100vh', padding: 24 }}>
            <Flex vertical gap="large" style={{ width: '100%', maxWidth: 440 }}>
              <Flex align="center" justify="center">
                <img
                  src={mouDarkLogo}
                  alt="Logo"
                  style={{ height: 32, width: 'auto', display: 'block' }}
                />

              </Flex>
              <Flex align="center" justify="center">
                <Title level={3}>{t('login.title')}</Title>
              </Flex>
              <Form
                form={form}
                name="loginForm"
                initialValues={{ remember: true }}
                onFinish={onFinish}
                layout="vertical"
                disabled={loading}
              >
                <Form.Item
                  name="phone"
                  label={t('login.phoneLabel')}
                  tooltip={t('login.phoneTooltip')}
                  rules={[
                    { required: true, message: t('login.phoneRequired') },
                    {
                      validator: (_, value) => {
                        const v = normalizePhone(value);
                        return v && isValidPhoneDigits(v)
                          ? Promise.resolve()
                          : Promise.reject(new Error(t('login.phoneInvalid')));
                      },
                    },
                  ]}
                >
                  <Input
                    ref={inputRef}
                    prefix={<PhoneOutlined />}
                    placeholder={t('login.phonePlaceholder')}
                    maxLength={15}
                    autoComplete="tel"
                    inputMode="numeric"
                    type="tel"
                    allowClear
                    autoFocus={isMobile}
                  />
                </Form.Item>
                <Row>
                  <Col xs={12} sm={12} style={{ textAlign: 'left' }}>
                    <Form.Item name="remember" valuePropName="checked">
                      <Checkbox>{t('login.rememberPhone')}</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} style={{ textAlign: 'right' }}>
                    <Select
                      value={i18n.language}
                      onChange={changeLanguage}
                      style={{ width: 120 }}
                      suffixIcon={<GlobalOutlined />}
                      options={[
                        { value: 'en', label: t('settings.english') },
                        { value: 'es', label: t('settings.spanish') },
                      ]}
                    />
                  </Col>
                </Row>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block loading={loading}>
                    {t('login.enterButton')}
                  </Button>
                </Form.Item>
              </Form>
            </Flex>
          </Flex>
        </Layout.Content>
      </Layout>
    </ConfigProvider>
  );
};

export default LoginPage;
